import type { ReactNode } from 'react';

import { useSearchParams } from 'next/navigation';
import { createContext, useContext, useMemo } from 'react';

import { listAllChildren } from '../../dom';
import type { Slot } from '~/amplienceTypes/content';
import type { ProductRecommendations as RecommendationsSchema } from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import type { RecommendationsProps } from '~/components/shared/recommendations/Recommendations';

type LayoutDesignContextType = {
  hasProductAccordions?: boolean;
  viaProductLabel?: boolean;
  completeTheLookProps?: RecommendationsSchema & RecommendationsProps;
};

const LayoutDesignContext = createContext<LayoutDesignContextType>({
  hasProductAccordions: undefined,
  viaProductLabel: false,
  completeTheLookProps: undefined,
});

const PRODUCT_LABEL = 'productLabel';

export const useLayoutDesignContext = () => useContext(LayoutDesignContext);

export const LayoutDesignContextProvider = ({
  children,
  layoutComponents = [],
  ...props
}: {
  children: ReactNode;
  layoutComponents?: Slot[];
} & LayoutDesignContextType) => {
  const searchParams = useSearchParams();

  const value = useMemo(() => {
    const layoutComponentsChildren = layoutComponents.reduce<Slot[]>(listAllChildren, []);

    return {
      hasProductAccordions: Boolean(
        layoutComponentsChildren.find(({ name }) => name === 'productAccordions')
      ),
      viaProductLabel: searchParams?.get('utm_campaign') === PRODUCT_LABEL,
      completeTheLookProps: layoutComponentsChildren.find(
        ({ name, properties }) =>
          name === 'recommendations' && properties?.content?.sectionName === 'complete the look'
      )?.properties?.content,
      ...props,
    };
  }, [layoutComponents, props, searchParams]);

  return <LayoutDesignContext.Provider value={value}>{children}</LayoutDesignContext.Provider>;
};
